import React from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { WithTranslation, withTranslation } from 'react-i18next';

export interface ActiveCarClass {
  acrissCode: string;
  name: string;
  rowNumber: number;
}

export interface PricePeriodsWithColumnIndex {
  period: string;
  columnNumber: number;
}

interface RatePlanAdjustmentModalProps extends WithTranslation {
  toggle: () => void;
  isOpen: boolean;
  ratePlanPeriods: PricePeriodsWithColumnIndex[];
  activeCarClasses: ActiveCarClass[];
  onAdjustmentConfirm: (selectedRows: number[], selectedColumns: number[], adjustBy: number) => void;
}

interface RatePlanAdjustmentModalState {
  selectedCarClasses: { name: string; acrissCode: string }[];
  selectedPeriods: string[];
  adjustBy: number;
  allCarsSelected: boolean;
  allPeriodsSelected: boolean;
}

class RatePlanAdjustmentModal extends React.PureComponent<RatePlanAdjustmentModalProps, RatePlanAdjustmentModalState> {

  readonly state = {
    selectedCarClasses: [] as { name: string; acrissCode: string }[],
    selectedPeriods: [] as string[],
    adjustBy: 0,
    allCarsSelected: true,
    allPeriodsSelected: false
  };

  componentDidUpdate(prevProps: RatePlanAdjustmentModalProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      this.toggleAllCarsSelected(false);
    }
  }

  toggleCarClassSelected = (name: string, acrissCode: string) => {
    const selectedClassesCopy: {
      name: string;
      acrissCode: string
    }[] = JSON.parse(JSON.stringify(this.state.selectedCarClasses));

    if (selectedClassesCopy.find((cc) => cc.name === name && cc.acrissCode === acrissCode)) {
      this.setState({ selectedCarClasses: selectedClassesCopy.filter((c) => c.name !== name && c.acrissCode !== acrissCode) });
    } else {
      selectedClassesCopy.push({ name, acrissCode });
      this.setState({ selectedCarClasses: selectedClassesCopy });
    }
  };

  toggleSelectedPeriod = (name: string) => {
    const selectedPeriodsCopy: string[] = JSON.parse(JSON.stringify(this.state.selectedPeriods));

    if (selectedPeriodsCopy.find((p) => p === name)) {
      this.setState({ selectedPeriods: selectedPeriodsCopy.filter((p) => p !== name) });
    } else {
      selectedPeriodsCopy.push(name);
      this.setState({ selectedPeriods: selectedPeriodsCopy });
    }
  };

  onConfirm = () => {
    const selectedRows: number[] = [];
    const selectedColumns: number[] = [];

    this.state.selectedCarClasses.forEach((sc) => {
      const carClass = this.props.activeCarClasses.find((cc) => sc.name === cc.name && sc.acrissCode === cc.acrissCode);

      if (carClass) selectedRows.push(carClass.rowNumber);
    });

    this.state.selectedPeriods.forEach((sp) => {
      const ratePlanPeriod = this.props.ratePlanPeriods.find((rp) => sp === rp.period);

      if (ratePlanPeriod) selectedColumns.push(ratePlanPeriod.columnNumber);
    });

    this.props.onAdjustmentConfirm(selectedRows, selectedColumns, this.state.adjustBy);
    this.setState({
      selectedCarClasses: [] as { name: string; acrissCode: string }[],
      selectedPeriods: [] as string[],
      adjustBy: 0,
      allCarsSelected: true,
      allPeriodsSelected: false
    });
    this.props.toggle();
  };

  onCancel = () => {
    this.setState({
      selectedCarClasses: [] as { name: string; acrissCode: string }[],
      selectedPeriods: [] as string[],
      adjustBy: 0,
      allCarsSelected: true,
      allPeriodsSelected: false
    });

    this.props.toggle();
  };

  toggleAllCarsSelected = (isSelected: boolean) => {
    if (isSelected) {
      this.setState({ selectedCarClasses: [], allCarsSelected: false });
      return;
    }

    const selectedCarClasses: { name: string; acrissCode: string }[] = [];
    this.props.activeCarClasses.forEach((ac) => {
      selectedCarClasses.push({ name: ac.name, acrissCode: ac.acrissCode });
    });

    this.setState({ selectedCarClasses, allCarsSelected: true });
  };

  toggleAllPeriodsSelected = (isSelected: boolean) => {
    if (isSelected) {
      this.setState({ selectedPeriods: [], allPeriodsSelected: false });
      return;
    }

    const selectedPeriods: string[] = [];
    this.props.ratePlanPeriods.forEach((p) => {
      selectedPeriods.push(p.period);
    });

    this.setState({ selectedPeriods, allPeriodsSelected: true });
  };

  getRatePlanPeriods = () => {
    return (
      this.props.ratePlanPeriods.map((p) => (
        <div key={p.period} className="custom-control custom-checkbox d-flex align-items-center ml-2">
          <input
            type="checkbox"
            className="custom-control-input"
            onClick={(e) => this.toggleSelectedPeriod(p.period)}
            checked={this.state.selectedPeriods.find((sp) => sp === p.period) !== undefined}
            id={p.period}
          />
          <label className="custom-control-label" htmlFor={p.period}>
            {p.period}
          </label>
        </div>
      )));
  };

  getCarClasses = () => {
    return (
      this.props.activeCarClasses.map((c) => (
        <div key={`${c.name}-${c.acrissCode}`}
             className="custom-control custom-checkbox d-flex align-items-center">
          <input
            type="checkbox"
            className="custom-control-input"
            onClick={(e) => this.toggleCarClassSelected(c.name, c.acrissCode)}
            checked={this.state.selectedCarClasses.find((cc) => cc.name === c.name && cc.acrissCode === c.acrissCode) !== undefined}
            id={`${c.name}-${c.acrissCode}`}
          />
          <label className="custom-control-label" htmlFor={`${c.name}-${c.acrissCode}`}>
            {`${c.acrissCode}   ${c.name}`}
          </label>
        </div>
      ))
    );
  };

  render() {
    const { t } = this.props;

    return (
      <Modal className="rate-adjustment-modal" isOpen={this.props.isOpen} toggle={this.props.toggle} autoFocus={false}>
        <ModalHeader toggle={this.props.toggle}>Adjust rates</ModalHeader>
        <ModalBody>
          <div className="d-flex">
            <div className="ml-3 mr-3">
              <div key="select-periods" className="custom-control custom-checkbox d-flex align-items-center ml-2">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  onClick={(e) => this.toggleAllPeriodsSelected(this.state.allPeriodsSelected)}
                  checked={this.state.allPeriodsSelected}
                  id="select-periods"
                />
                <label className="custom-control-label" htmlFor="select-periods">
                  {t('b.dual_filter.select_all')}
                </label>
              </div>
              {this.getRatePlanPeriods()}
            </div>
            <div className="scrolling-modal-div-large ml-2 mr-2">
              <div key="select-cars" className="custom-control custom-checkbox d-flex align-items-center">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  onClick={(e) => this.toggleAllCarsSelected(this.state.allCarsSelected)}
                  checked={this.state.allCarsSelected}
                  id="select-cars"
                />
                <label className="custom-control-label" htmlFor="select-cars">
                  {t('b.dual_filter.select_all')}
                </label>
              </div>
              {this.getCarClasses()}
            </div>
          </div>
          <div className="d-flex justify-content-start align-items-center mt-2 ml-2">
            <span>{t('l.rate_plan.adjust_rates_by')}</span>
            <div className="col-2 d-flex align-items-center justify-content-start">
              <input
                defaultValue={this.state.adjustBy}
                type="number"
                onChange={(e) => this.setState({ adjustBy: Number(e.target.value) })}
                className="form-control"
              />
            </div>
            <span>%</span>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex align-items-center justify-content-end">
          <button type="button" className="mr-1 btn btn-outline-secondary" color="secondary"
                  onClick={() => this.onCancel()}>{t('b.cancel_Generic cancel button translation').toUpperCase()}</button>
          <button type="submit" className="btn btn-primary" color="primary" onClick={() => (this.onConfirm())}>
            {t('b.confirm').toUpperCase()}
          </button>
        </ModalFooter>
      </Modal>
    );
  }
}

export default withTranslation()(RatePlanAdjustmentModal);
